import { useFormikContext } from 'formik';

import {
  RelativePathModel,
  GitFormModel,
} from '@/react/portainer/gitops/types';
import { HelmInstallOptionsSection } from '@/react/edge/edge-stacks/CreateView/HelmChartForm/HelmInstallOptionsSection';
import { HelmChartPathSection } from '@/react/edge/edge-stacks/CreateView/HelmChartForm/HelmChartPathSection';
import { HelmValuesFilesSection } from '@/react/edge/edge-stacks/CreateView/HelmChartForm/HelmValuesFilesSection';

import { FormSection } from '@@/form-components/FormSection';
import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';

interface HelmDeploymentFieldsetFormValues {
  helmChartPath?: string;
  helmVersion?: string;
  helmValuesFiles?: string[];
  helmNamespace?: string;
}

interface HelmDeploymentFieldsetProps {
  stackName: string;
  relativePath?: RelativePathModel;
  gitModel: GitFormModel;
}

function getDeviceMatchTypeLabel(matchType: 'file' | 'dir' | ''): string {
  if (matchType === 'file') {
    return 'Match file name with Portainer Edge ID';
  }
  if (matchType === 'dir') {
    return 'Match folder name with Portainer Edge ID';
  }
  return '(Not configured)';
}

function getGroupMatchTypeLabel(matchType: 'file' | 'dir' | ''): string {
  if (matchType === 'file') {
    return 'Match file name with Edge Group';
  }
  if (matchType === 'dir') {
    return 'Match folder name with Edge Group';
  }
  return '(Not configured)';
}

export function HelmDeploymentFieldset({
  stackName,
  relativePath,
  gitModel,
}: HelmDeploymentFieldsetProps) {
  const {
    values: formValues,
    setFieldValue,
    errors,
  } = useFormikContext<HelmDeploymentFieldsetFormValues>();

  return (
    <>
      <FormSection title="Deploy to">
        <FormControl label="Release name" inputId="helm-release-name">
          <Input
            id="helm-release-name"
            value={`edge-${stackName}`}
            readOnly
            disabled
            data-cy="helm-release-name-input"
          />
        </FormControl>

        <FormControl
          label="Namespace"
          inputId="helm-namespace"
          tooltip="Kubernetes namespace to deploy the Helm chart into"
        >
          <Input
            id="helm-namespace"
            value={formValues.helmNamespace || ''}
            onChange={(e) => setFieldValue('helmNamespace', e.target.value)}
            placeholder="e.g. default"
            data-cy="helm-namespace-input"
          />
        </FormControl>
      </FormSection>

      <HelmChartPathSection
        chartPath={formValues.helmChartPath || ''}
        gitModel={gitModel}
        errors={errors}
        onChartPathChange={(value) => setFieldValue('helmChartPath', value)}
      />

      {relativePath?.SupportPerDeviceConfigs && (
        <FormSection title="GitOps Edge configurations">
          <FormControl
            label="Configuration path"
            inputId="helm-per-device-config-path"
            tooltip="Directory in the Git repository containing per-device/group configuration files"
          >
            <Input
              id="helm-per-device-config-path"
              value={relativePath.PerDeviceConfigsPath || '(Not configured)'}
              readOnly
              disabled
              data-cy="helm-per-device-config-path-input"
            />
          </FormControl>

          {relativePath.PerDeviceConfigsMatchType && (
            <FormControl
              label="Device matching rule"
              inputId="helm-per-device-match-type"
              tooltip="How device-specific configurations are matched"
            >
              <Input
                id="helm-per-device-match-type"
                value={getDeviceMatchTypeLabel(
                  relativePath.PerDeviceConfigsMatchType
                )}
                readOnly
                disabled
                data-cy="helm-per-device-match-type-input"
              />
            </FormControl>
          )}

          {relativePath.PerDeviceConfigsGroupMatchType && (
            <FormControl
              label="Group matching rule"
              inputId="helm-per-device-group-match-type"
              tooltip="How group-specific configurations are matched"
            >
              <Input
                id="helm-per-device-group-match-type"
                value={getGroupMatchTypeLabel(
                  relativePath.PerDeviceConfigsGroupMatchType
                )}
                readOnly
                disabled
                data-cy="helm-per-device-group-match-type-input"
              />
            </FormControl>
          )}
        </FormSection>
      )}

      <FormSection title="Install options">
        <FormControl
          label="Values files"
          tooltip="User-defined values files to override the default chart values. Paths are relative to the Helm chart folder in your Git repository."
        >
          <HelmValuesFilesSection
            valuesFiles={formValues.helmValuesFiles || []}
            gitModel={gitModel}
            errors={errors}
            onValuesFileChange={(index, value) => {
              const updated = [...(formValues.helmValuesFiles || [])];
              updated[index] = value;
              setFieldValue('helmValuesFiles', updated);
            }}
            variant="edit"
            relativePath={relativePath}
          />
        </FormControl>

        <HelmInstallOptionsSection />
      </FormSection>
    </>
  );
}
